/**
 * Tunnel system — 3D wireframe tunnel for Complex (ℂ) mode.
 *
 * Generates and renders a cylinder of rings scrolling toward the player.
 * Supports circular, rose, and lemniscate tunnel cross-sections.
 * Includes 3D obstacle rendering (rings to pass through, grids).
 */

import type { Renderer } from '@/rendering/renderer';
import type { Color } from '@/types';

// ─── Tunnel Geometry ───────────────────────────────────────

const RING_SEGMENTS = 24;         // Vertices per ring
const RING_SPACING = 20;          // Z distance between rings
const VISIBLE_RINGS = 30;         // How many rings to render

export interface TunnelObstacle3D {
  type: 'ring' | 'grid';
  z: number;                      // Z position along tunnel
  // Ring: gap angle and size
  gapAngle?: number;              // Where the gap is (radians)
  gapSize?: number;               // Gap angular width (radians)
  // Grid: rows and cols with gap positions
  gridGapX?: number;
  gridGapY?: number;
  color: Color;
  passed: boolean;
}

export class TunnelRenderer {
  /** Base tunnel radius */
  radius = 80;

  /** Tunnel shape (0 = circle, blends toward rose at higher values) */
  shapeMorph = 0;

  /** Rose curve petal count (for morph) */
  rosePetals = 3;

  /** How far the player has scrolled into the tunnel */
  scrollZ = 0;

  /** Tunnel color */
  color: Color = { r: 0, g: 1, b: 1, a: 0.5 };

  /** Ring line color */
  ringColor: Color = { r: 0, g: 1, b: 1, a: 0.3 };

  /** Longitudinal line color */
  lineColor: Color = { r: 0, g: 1, b: 1, a: 0.15 };

  /** Get the tunnel radius at a given angle (supports shape morphing) */
  getRadius(angle: number): number {
    if (this.shapeMorph <= 0) return this.radius;
    // Blend between circle and rose curve: r = cos(n*θ)
    const rose = this.radius * (0.6 + 0.4 * Math.abs(Math.cos(this.rosePetals * angle)));
    return this.radius * (1 - this.shapeMorph) + rose * this.shapeMorph;
  }

  /** Render the wireframe tunnel */
  render(renderer: Renderer): void {
    // baseZ creates smooth scrolling — rings slide toward the camera
    const baseZ = ((this.scrollZ % RING_SPACING) + RING_SPACING) % RING_SPACING;

    // Draw rings receding into the distance (-Z direction)
    for (let ri = 0; ri < VISIBLE_RINGS; ri++) {
      const z = -(baseZ + ri * RING_SPACING + 10); // +10 offset so nearest ring isn't clipped
      this.drawRing(renderer, z, this.ringColor);
    }

    // Draw longitudinal lines (connect rings along Z)
    const longCount = 12;
    for (let li = 0; li < longCount; li++) {
      const angle = (Math.PI * 2 * li) / longCount;
      const r = this.getRadius(angle);
      const x = Math.cos(angle) * r;
      const y = Math.sin(angle) * r;

      for (let ri = 0; ri < VISIBLE_RINGS - 1; ri++) {
        const z1 = -(baseZ + ri * RING_SPACING + 10);
        const z2 = -(baseZ + (ri + 1) * RING_SPACING + 10);
        renderer.drawLine3d(x, y, z1, x, y, z2, 0.8, this.lineColor);
      }
    }
  }

  /** Draw a single ring at z depth */
  private drawRing(renderer: Renderer, z: number, color: Color): void {
    for (let i = 0; i < RING_SEGMENTS; i++) {
      const a1 = (Math.PI * 2 * i) / RING_SEGMENTS;
      const a2 = (Math.PI * 2 * (i + 1)) / RING_SEGMENTS;
      const r1 = this.getRadius(a1);
      const r2 = this.getRadius(a2);

      renderer.drawLine3d(
        Math.cos(a1) * r1, Math.sin(a1) * r1, z,
        Math.cos(a2) * r2, Math.sin(a2) * r2, z,
        1.0, color,
      );
    }
  }

  /** Render a ring obstacle (ring with a gap to fly through) */
  renderObstacle(renderer: Renderer, obs: TunnelObstacle3D): void {
    const z = -(obs.z - this.scrollZ + 10);
    if (z > 0 || z < -VISIBLE_RINGS * RING_SPACING) return;

    if (obs.type === 'ring') {
      this.renderRingObstacle(renderer, z, obs);
    } else if (obs.type === 'grid') {
      this.renderGridObstacle(renderer, z, obs);
    }
  }

  private renderRingObstacle(renderer: Renderer, z: number, obs: TunnelObstacle3D): void {
    const gapAngle = obs.gapAngle ?? 0;
    const gapSize = obs.gapSize ?? Math.PI * 0.4;
    const r = this.radius * 0.85; // Slightly inside tunnel

    for (let i = 0; i < RING_SEGMENTS; i++) {
      const a1 = (Math.PI * 2 * i) / RING_SEGMENTS;
      const a2 = (Math.PI * 2 * (i + 1)) / RING_SEGMENTS;

      // Skip segments in the gap
      const midAngle = (a1 + a2) / 2;
      let diff = midAngle - gapAngle;
      // Normalize to [-π, π]
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      if (Math.abs(diff) < gapSize / 2) continue;

      renderer.drawLine3d(
        Math.cos(a1) * r, Math.sin(a1) * r, z,
        Math.cos(a2) * r, Math.sin(a2) * r, z,
        2.0, obs.color,
      );
    }
  }

  private renderGridObstacle(renderer: Renderer, z: number, obs: TunnelObstacle3D): void {
    const r = this.radius * 0.85;
    const gapX = obs.gridGapX ?? 0;
    const gapY = obs.gridGapY ?? 0;
    const gridSize = 5;
    const spacing = r * 2 / gridSize;

    // Horizontal lines
    for (let row = -gridSize / 2; row <= gridSize / 2; row++) {
      const y = row * spacing;
      if (Math.abs(y - gapY) < spacing * 1.2) continue; // gap
      renderer.drawLine3d(-r, y, z, r, y, z, 1.5, obs.color);
    }

    // Vertical lines
    for (let col = -gridSize / 2; col <= gridSize / 2; col++) {
      const x = col * spacing;
      if (Math.abs(x - gapX) < spacing * 1.2) continue; // gap
      renderer.drawLine3d(x, -r, z, x, r, z, 1.5, obs.color);
    }
  }

  /** Check if a player position collides with a ring obstacle */
  checkCollision(playerX: number, playerY: number, playerRadius: number, obs: TunnelObstacle3D): boolean {
    if (obs.passed) return false;
    if (obs.type !== 'ring') return false;

    const gapAngle = obs.gapAngle ?? 0;
    const gapSize = obs.gapSize ?? Math.PI * 0.4;
    const r = this.radius * 0.85;

    // Player angle in the tunnel cross-section
    const playerAngle = Math.atan2(playerY, playerX);
    let diff = playerAngle - gapAngle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;

    // Player is in the gap — safe
    if (Math.abs(diff) < gapSize / 2) return false;

    // Player is outside the ring radius — safe
    const playerDist = Math.sqrt(playerX * playerX + playerY * playerY);
    if (playerDist + playerRadius < r - 5) return false;

    // Player hit the ring
    return true;
  }

  /** Render the 3D player (small triangle at position in tunnel cross-section) */
  renderPlayer3d(renderer: Renderer, x: number, y: number, color: Color): void {
    const s = 6;
    const z = -15; // Near the camera but not clipped
    // Glow (behind)
    const gs = s * 1.6;
    const glowColor: Color = { ...color, a: 0.25 };
    renderer.drawTriangle3d(
      x, y + gs, z,
      x - gs * 0.866, y - gs * 0.5, z,
      x + gs * 0.866, y - gs * 0.5, z,
      glowColor,
    );
    // Body
    renderer.drawTriangle3d(
      x, y + s, z,
      x - s * 0.866, y - s * 0.5, z,
      x + s * 0.866, y - s * 0.5, z,
      color,
    );
  }
}
